import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, Plus, Snowflake, PlayCircle } from "lucide-react";
import { format } from "date-fns";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { useAppMode } from "@/hooks/useAppMode";
import { useBookings } from "@/hooks/useBookings";
import { useUserMemberships } from "@/hooks/useUserMemberships";

const AppHome = () => {
  const navigate = useNavigate();
  const { provider } = useAppMode();
  const { data: bookings = [] } = useBookings();
  const { data: memberships = [] } = useUserMemberships(provider?.id);

  const brandColor = provider?.brand_primary_color || undefined;

  // Filter to this provider only
  const providerBookings = bookings.filter(
    (b) => b.provider_id === provider?.id
  );

  const now = new Date();

  // Active session = checked in but not checked out and not cancelled/completed
  const activeSession = providerBookings.find((b) => {
    const status = b.status.toLowerCase();
    if (status === "cancelled" || status === "completed") return false;
    return Boolean((b as any).checked_in_at) && !(b as any).checked_out_at;
  });

  const upcoming = providerBookings
    .filter((b) => {
      if (b.status.toLowerCase() === "cancelled") return false;
      if (b.status.toLowerCase() === "completed") return false;
      if (activeSession && b.id === activeSession.id) return false;
      const [h, m] = b.start_time.split(":").map(Number);
      const dt = new Date(b.booking_date);
      dt.setHours(h, m, 0, 0);
      return dt >= now;
    })
    .sort((a, b) => {
      const dtA = new Date(`${a.booking_date}T${a.start_time}`);
      const dtB = new Date(`${b.booking_date}T${b.start_time}`);
      return dtA.getTime() - dtB.getTime();
    });

  const nextBooking = upcoming[0];
  const heroBooking = activeSession || nextBooking;
  const isActive = Boolean(activeSession);

  const formatTime = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h >= 12 ? "PM" : "AM";
    return `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  return (
    <MobileAppLayout title="Home">
      <div className="px-4 py-5 space-y-5">
        {/* Greeting */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isActive
              ? "Your session is in progress."
              : nextBooking
              ? "You have an upcoming session."
              : "Ready for your next session?"}
          </p>
        </div>

        {/* Hero: active session takes priority over next booking */}
        {heroBooking ? (
          <Card
            className="overflow-hidden border-0 text-white relative"
            style={{
              backgroundColor: brandColor || "hsl(var(--primary))",
            }}
          >
            {isActive && (
              <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-white/20 backdrop-blur rounded-full px-2.5 py-1">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] font-semibold uppercase tracking-wider">Live</span>
              </div>
            )}
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider opacity-80 mb-2 flex items-center gap-1.5">
                {isActive ? (
                  <>
                    <PlayCircle className="h-3.5 w-3.5" />
                    Active session
                  </>
                ) : (
                  "Next session"
                )}
              </p>
              <h3 className="font-semibold text-lg mb-3">
                {heroBooking.service_name}
              </h3>
              <div className="space-y-2 text-sm opacity-95">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {format(new Date(heroBooking.booking_date), "EEE, MMM d")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>
                    {formatTime(heroBooking.start_time)} –{" "}
                    {formatTime(heroBooking.end_time)}
                  </span>
                </div>
              </div>
              <Button
                variant="secondary"
                className="w-full mt-4"
                onClick={() => navigate(`/app/booking/${heroBooking.id}`)}
              >
                {isActive ? "Manage session" : "View details"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6 text-center">
              <div
                className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                style={{
                  backgroundColor: brandColor
                    ? `${brandColor}20`
                    : "hsl(var(--accent))",
                }}
              >
                <Snowflake
                  className="h-6 w-6"
                  style={{ color: brandColor }}
                />
              </div>
              <h3 className="font-semibold mb-1">No upcoming sessions</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Book your next session in seconds.
              </p>
              <Button
                className="w-full"
                style={
                  brandColor ? { backgroundColor: brandColor } : undefined
                }
                onClick={() => navigate("/app/book")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Book a session
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Show next session below if an active session is being shown */}
        {isActive && nextBooking && (
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Up next
            </h2>
            <Card
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/app/booking/${nextBooking.id}`)}
            >
              <CardContent className="p-4">
                <p className="font-semibold text-sm mb-2">
                  {nextBooking.service_name}
                </p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {format(new Date(nextBooking.booking_date), "EEE, MMM d")}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {formatTime(nextBooking.start_time)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Active memberships & packages */}
        {memberships.length > 0 && (() => {
          const hasMembership = memberships.some(
            (m) => m.membership.membership_type !== "pack_of_sessions"
          );
          const hasPackage = memberships.some(
            (m) => m.membership.membership_type === "pack_of_sessions"
          );
          const heading =
            hasMembership && hasPackage
              ? "Your memberships & packages"
              : hasPackage
              ? memberships.filter(
                  (m) => m.membership.membership_type === "pack_of_sessions"
                ).length > 1
                ? "Your packages"
                : "Your package"
              : memberships.filter(
                  (m) => m.membership.membership_type !== "pack_of_sessions"
                ).length > 1
              ? "Your memberships"
              : "Your membership";
          return (
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {heading}
              </h2>
              <div className="space-y-2">
                {memberships.map((m) => (
                  <Card key={m.id}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{m.membership.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {m.membership.membership_type === "pack_of_sessions"
                            ? `${m.sessions_remaining ?? 0} sessions left`
                            : "Active subscription"}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Quick action */}
        {nextBooking && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate("/app/book")}
          >
            <Plus className="h-4 w-4 mr-2" />
            Book another session
          </Button>
        )}
      </div>
    </MobileAppLayout>
  );
};

export default AppHome;
